/**
 * Forces proper case (Title Case) on a name string.
 * Capitalizes the first letter of each word, lowercases the rest.
 * Handles hyphenated names (e.g., "mary-jane" → "Mary-Jane")
 * and apostrophes (e.g., "o'brien" → "O'Brien").
 */
export function properCaseName(value: string): string {
  return value
    .replace(/(?:^|[\s\-'])([a-zA-Z])/g, (match) => match.toUpperCase())
    .replace(
      /(?<=[\s\-'].[a-zA-Z]*)([A-Z])/g,
      // Only lowercase letters that aren't the first after a separator
      (_match, _p1, offset, str) => {
        // Check if this uppercase letter is immediately after a separator+letter
        const before = str.slice(0, offset);
        const lastSep = Math.max(
          before.lastIndexOf(' '),
          before.lastIndexOf('-'),
          before.lastIndexOf("'"),
        );
        if (offset - lastSep <= 1) return str[offset]; // This IS the first letter — keep upper
        return str[offset].toLowerCase();
      },
    );
}

/**
 * Simpler approach: split on word boundaries, capitalize first letter of each segment.
 * Preserves user's typing flow while enforcing proper case.
 */
export function toProperCase(value: string): string {
  return value
    .split(/(\s+|-|')/)
    .map((segment) => {
      if (/^[\s\-']+$/.test(segment)) return segment; // separator — keep as-is
      if (segment.length === 0) return segment;
      return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
    })
    .join('');
}
